<script lang="ts">
import { RouteNames } from "../../../../utils/constants.ts";

export function getRouteXAccountsCryptoBuy() {
  return {
    name: RouteNames.X_BUY_CRYPTO,
  };
}
</script>
<script setup lang="ts">
import BreadCrumbs from "../../../../components/BreadCrumbs.vue";
import BasicCard from "../../../../components/BasicCard.vue";
import TextInput from "../../../../components/form/TextInput.vue";
import InputSelect from "../../../../components/form/InputSelect.vue";
import { ref, computed, watch } from "vue";
import { 
  ArrowRight as ArrowRightIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  Check as CheckIcon,
  AlertCircle as AlertCircleIcon,
  CreditCard as CreditCardIcon,
  Zap as ZapIcon
} from "lucide-vue-next";

// Available cryptocurrencies with mock prices
const cryptocurrencies = {
  "btc": "Bitcoin (BTC)",
  "eth": "Ethereum (ETH)", 
  "usdt": "Tether (USDT)",
  "usdc": "USD Coin (USDC)",
  "ada": "Cardano (ADA)",
  "sol": "Solana (SOL)",
  "dot": "Polkadot (DOT)",
  "link": "Chainlink (LINK)"
};

// Payment methods
const paymentMethods = {
  "card_1234": "•••• 1234 (Visa)",
  "card_5678": "•••• 5678 (Mastercard)", 
  "bank_acc": "Bank Account (••••1234)",
  "paypal": "PayPal Account"
};

// Form state
const selectedCrypto = ref("btc");
const paymentMethod = ref("card_1234");
const buyAmount = ref("");
const receiveAmount = ref("");
const inputMode = ref("fiat"); // "fiat" or "crypto"

// UI state
const step = ref(1); // 1: buy form, 2: payment, 3: confirmation
const isLoading = ref(false);
const transactionId = ref("");

// Mock price data
const cryptoPrices = {
  "btc": 45000,
  "eth": 3200,
  "usdt": 1,
  "usdc": 1,
  "ada": 0.85,
  "sol": 125,
  "dot": 35,
  "link": 28
};

// Mock fees
const fees = {
  processing: 2.5, // percentage
  network: {
    "btc": 0.0002,
    "eth": 0.005,
    "usdt": 1,
    "usdc": 1,
    "ada": 0.17,
    "sol": 0.001,
    "dot": 0.01,
    "link": 0.001
  }
};

// Computed values
const currentPrice = computed(() => {
  return cryptoPrices[selectedCrypto.value as keyof typeof cryptoPrices] || 0;
});

const processingFee = computed(() => {
  const amount = parseFloat(buyAmount.value) || 0;
  return (amount * fees.processing / 100).toFixed(2);
});

const networkFee = computed(() => {
  return fees.network[selectedCrypto.value as keyof typeof fees.network] || 0;
});

const totalCost = computed(() => {
  const amount = parseFloat(buyAmount.value) || 0;
  const fee = parseFloat(processingFee.value) || 0;
  return (amount + fee).toFixed(2);
});

const getCryptoSymbol = () => {
  const symbols = {
    "btc": "BTC",
    "eth": "ETH",
    "usdt": "USDT", 
    "usdc": "USDC",
    "ada": "ADA",
    "sol": "SOL",
    "dot": "DOT",
    "link": "LINK"
  };
  return symbols[selectedCrypto.value as keyof typeof symbols] || "";
};

// Calculations
const calculateCryptoAmount = (fiatAmount: number) => {
  return (fiatAmount / currentPrice.value).toFixed(8);
};

const calculateFiatAmount = (cryptoAmount: number) => {
  return (cryptoAmount * currentPrice.value).toFixed(2);
};

// Watchers for live conversion
watch([buyAmount, selectedCrypto], () => {
  if (inputMode.value === "fiat" && buyAmount.value) {
    const fiatAmount = parseFloat(buyAmount.value);
    if (!isNaN(fiatAmount)) {
      receiveAmount.value = calculateCryptoAmount(fiatAmount);
    }
  }
});

watch([receiveAmount, selectedCrypto], () => {
  if (inputMode.value === "crypto" && receiveAmount.value) {
    const cryptoAmount = parseFloat(receiveAmount.value);
    if (!isNaN(cryptoAmount)) {
      buyAmount.value = calculateFiatAmount(cryptoAmount);
    }
  }
});

// Form handlers
const switchInputMode = () => {
  inputMode.value = inputMode.value === "fiat" ? "crypto" : "fiat";
  // Swap the values
  const temp = buyAmount.value;
  buyAmount.value = receiveAmount.value;
  receiveAmount.value = temp;
};

const setQuickAmount = (amount: number) => {
  inputMode.value = "fiat";
  buyAmount.value = amount.toString();
  receiveAmount.value = calculateCryptoAmount(amount);
};

const validateForm = () => {
  const amount = parseFloat(buyAmount.value);
  return !isNaN(amount) && amount >= 10 && amount <= 10000;
};

const proceedToPayment = () => {
  if (validateForm()) {
    step.value = 2;
  }
};

const confirmPurchase = async () => {
  isLoading.value = true;
  
  // Simulate API call
  setTimeout(() => {
    transactionId.value = "TXN" + Math.random().toString(36).substring(2, 11).toUpperCase();
    step.value = 3;
    isLoading.value = false;
  }, 3000);
};

const goBack = () => {
  if (step.value > 1) {
    step.value--;
  }
};

const startNewPurchase = () => {
  step.value = 1;
  buyAmount.value = "";
  receiveAmount.value = "";
  transactionId.value = "";
};

// Popular cryptocurrencies with trend data
const popularCrypto = [
  { symbol: "BTC", name: "Bitcoin", price: "$45,000", change: "+2.5%", trending: "up" },
  { symbol: "ETH", name: "Ethereum", price: "$3,200", change: "+1.8%", trending: "up" },
  { symbol: "ADA", name: "Cardano", price: "$0.85", change: "-0.5%", trending: "down" },
  { symbol: "SOL", name: "Solana", price: "$125", change: "+5.2%", trending: "up" }
];
</script>

<template>
  <BreadCrumbs :title="$t('Buy Crypto')" class="mb-5" />

  <div class="max-w-2xl mx-auto">
    <!-- Step Indicator -->
    <div class="flex justify-center mb-8">
      <div class="flex items-center space-x-4">
        <div class="flex items-center">
          <div :class="[step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            1
          </div>
          <span class="ml-2 text-sm">Select & Amount</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            2
          </div>
          <span class="ml-2 text-sm">Payment</span>
        </div>
        <arrow-right-icon class="h-4 w-4 text-gray-400" />
        <div class="flex items-center">
          <div :class="[step >= 3 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600', 'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium']">
            <check-icon v-if="step >= 3" class="h-4 w-4" />
            <span v-else>3</span>
          </div>
          <span class="ml-2 text-sm">Confirm</span>
        </div>
      </div>
    </div>

    <!-- Step 1: Buy Form -->
    <div v-if="step === 1" class="space-y-6">
      <!-- Popular Cryptocurrencies -->
      <BasicCard>
        <div class="flex items-center gap-3 mb-6">
          <trending-up-icon class="h-6 w-6 text-green-600" />
          <h2 class="text-xl font-semibold">Popular Cryptocurrencies</h2>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            v-for="crypto in popularCrypto" 
            :key="crypto.symbol"
            @click="selectedCrypto = crypto.symbol.toLowerCase()"
            :class="[
              'p-4 border rounded-lg cursor-pointer transition-all',
              selectedCrypto === crypto.symbol.toLowerCase() 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            ]"
          >
            <div class="text-center">
              <div class="font-semibold">{{ crypto.symbol }}</div>
              <div class="text-sm text-gray-600">{{ crypto.name }}</div>
              <div class="text-sm font-medium mt-1">{{ crypto.price }}</div>
              <div :class="[
                'text-xs',
                crypto.trending === 'up' ? 'text-green-600' : 'text-red-600'
              ]">
                {{ crypto.change }}
              </div>
            </div>
          </div>
        </div>
      </BasicCard>

      <!-- Buy Form -->
      <BasicCard>
        <div class="flex items-center gap-3 mb-6">
          <shopping-cart-icon class="h-6 w-6 text-blue-600" />
          <h2 class="text-xl font-semibold">Buy Cryptocurrency</h2>
        </div>

        <form @submit.prevent="proceedToPayment" class="space-y-6">
          <!-- Cryptocurrency Selection -->
          <div>
            <label class="text-sm text-gray-600 mb-2 block">Select Cryptocurrency</label>
            <InputSelect 
              v-model="selectedCrypto" 
              :list="cryptocurrencies"
              placeholder="Choose cryptocurrency"
            />
            <p class="text-sm text-gray-500 mt-1">
              Current price: ${{ currentPrice.toLocaleString() }}
            </p>
          </div>

          <!-- Amount Input Section -->
          <div class="space-y-4">
            <!-- Quick Amount Buttons -->
            <div>
              <label class="text-sm text-gray-600 mb-2 block">Quick Amount</label>
              <div class="flex gap-2 mb-4">
                <button
                  v-for="amount in [50, 100, 250, 500]"
                  :key="amount"
                  type="button"
                  @click="setQuickAmount(amount)"
                  class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  ${{ amount }}
                </button>
              </div>
            </div>

            <!-- Spend Amount -->
            <div>
              <div class="flex justify-between items-center mb-2">
                <label class="text-sm text-gray-600">You Pay</label>
                <button
                  type="button"
                  @click="switchInputMode"
                  class="text-xs text-blue-600 hover:text-blue-700"
                >
                  Switch to {{ inputMode === 'fiat' ? 'crypto' : 'USD' }}
                </button>
              </div>
              <div class="relative">
                <TextInput
                  v-model="buyAmount"
                  name="buyAmount"
                  type="number"
                  :placeholder="inputMode === 'fiat' ? 'Enter USD amount' : `Enter ${getCryptoSymbol()} amount`"
                  class="pr-16"
                />
                <div class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                  {{ inputMode === 'fiat' ? 'USD' : getCryptoSymbol() }}
                </div>
              </div>
            </div>

            <!-- Conversion Arrow -->
            <div class="flex justify-center">
              <div class="p-2 bg-gray-100 rounded-full">
                <arrow-right-icon class="h-4 w-4 text-gray-600 transform rotate-90" />
              </div>
            </div>

            <!-- Receive Amount -->
            <div>
              <label class="text-sm text-gray-600 mb-2 block">You Receive</label>
              <div class="relative">
                <TextInput
                  v-model="receiveAmount"
                  name="receiveAmount"
                  type="text"
                  :placeholder="`${getCryptoSymbol()} amount`"
                  readonly
                  class="bg-gray-50 pr-16"
                />
                <div class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                  {{ inputMode === 'fiat' ? getCryptoSymbol() : 'USD' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Fee Breakdown -->
          <div v-if="buyAmount && !isNaN(parseFloat(buyAmount))" class="bg-gray-50 p-4 rounded-lg space-y-2">
            <h3 class="font-medium text-gray-800 mb-3">Fee Breakdown</h3>
            <div class="flex justify-between text-sm">
              <span>Purchase Amount</span>
              <span>${{ parseFloat(buyAmount).toFixed(2) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span>Processing Fee ({{ fees.processing }}%)</span>
              <span>${{ processingFee }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span>Network Fee</span>
              <span>{{ networkFee }} {{ getCryptoSymbol() }}</span>
            </div>
            <div class="border-t pt-2 flex justify-between font-medium">
              <span>Total Cost</span>
              <span>${{ totalCost }}</span>
            </div>
          </div>

          <!-- Validation Messages -->
          <div v-if="buyAmount && parseFloat(buyAmount) < 10" class="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <alert-circle-icon class="h-5 w-5 text-yellow-600" />
            <span class="text-sm text-yellow-800">Minimum purchase amount is $10</span>
          </div>

          <div v-if="buyAmount && parseFloat(buyAmount) > 10000" class="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <alert-circle-icon class="h-5 w-5 text-red-600" />
            <span class="text-sm text-red-800">Maximum purchase amount is $10,000</span>
          </div>

          <!-- Action Button -->
          <div class="flex justify-end pt-4">
            <button
              type="submit"
              :disabled="!validateForm()"
              class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Continue to Payment
            </button>
          </div>
        </form>
      </BasicCard>
    </div>

    <!-- Step 2: Payment -->
    <BasicCard v-if="step === 2">
      <div class="space-y-6">
        <div class="flex items-center gap-3 mb-6">
          <credit-card-icon class="h-6 w-6 text-green-600" />
          <h2 class="text-xl font-semibold">Payment Method</h2>
        </div>

        <!-- Order Summary -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 class="font-medium text-blue-800 mb-3">Order Summary</h3>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Cryptocurrency</span>
              <span>{{ cryptocurrencies[selectedCrypto as keyof typeof cryptocurrencies] }}</span>
            </div>
            <div class="flex justify-between">
              <span>Amount</span>
              <span>{{ receiveAmount }} {{ getCryptoSymbol() }}</span>
            </div>
            <div class="flex justify-between">
              <span>Rate</span>
              <span>1 {{ getCryptoSymbol() }} = ${{ currentPrice.toLocaleString() }}</span>
            </div>
            <div class="border-t pt-2 flex justify-between font-medium">
              <span>Total Cost</span>
              <span>${{ totalCost }}</span>
            </div>
          </div>
        </div>

        <!-- Payment Method Selection -->
        <div>
          <label class="text-sm text-gray-600 mb-2 block">Select Payment Method</label>
          <InputSelect 
            v-model="paymentMethod" 
            :list="paymentMethods"
            placeholder="Choose payment method"
          />
        </div>

        <!-- Security Notice -->
        <div class="bg-green-50 border border-green-200 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <zap-icon class="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <h3 class="font-medium text-green-800 mb-1">Instant Purchase</h3>
              <p class="text-sm text-green-700">
                Your cryptocurrency will be available in your wallet within minutes after payment confirmation.
              </p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between pt-6">
          <button
            @click="goBack"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <button
            @click="confirmPurchase"
            :disabled="isLoading"
            class="px-8 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="isLoading">Processing Payment...</span>
            <span v-else>Confirm Purchase</span>
          </button>
        </div>
      </div>
    </BasicCard>

    <!-- Step 3: Confirmation -->
    <BasicCard v-if="step === 3">
      <div class="text-center space-y-6">
        <div class="flex justify-center">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <check-icon class="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div>
          <h2 class="text-2xl font-semibold text-green-800 mb-2">Purchase Successful!</h2>
          <p class="text-gray-600">
            Your cryptocurrency has been purchased and will appear in your wallet shortly.
          </p>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg">
          <p class="text-sm text-gray-600 mb-2">Transaction ID</p>
          <code class="text-sm font-mono bg-white p-2 rounded border block">
            {{ transactionId }}
          </code>
        </div>

        <div class="space-y-2">
          <p class="font-medium">{{ receiveAmount }} {{ getCryptoSymbol() }} purchased</p>
          <p class="text-sm text-gray-600">Total paid: ${{ totalCost }}</p>
          <p class="text-sm text-gray-500">Estimated arrival: 2-5 minutes</p>
        </div>

        <div class="flex justify-center gap-4 pt-6">
          <button
            @click="startNewPurchase"
            class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Buy More
          </button>
          <button
            @click="$router.push({ name: 'account.crypto' })"
            class="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            View Portfolio
          </button>
        </div>
      </div>
    </BasicCard>
  </div>
</template>

<style scoped lang="scss">

</style>
